// Components
import CheckboxInput from "components/inputs/checkbox"
import DropdownInput from "components/inputs/dropdown"
import { CashFlowReport_s } from 'components/property/reports/report-styles'
import { parseDate } from "data/lib/dates"
import propertyReportService from "data/reports-property"
// Data
import underwritingService from "data/underwriting"
import _ from 'lodash'
import calcService from "data/calc"
import React, { FC, useEffect, useState } from "react"
// Styles
import { ButtonWithPress_s, Label_s } from "styles/app-styles"
import { InputNumber_s } from "styles/input-styles"
import { DateText_s, ReportForm_s } from "styles/report-styles"

interface CashFlowProps {
  property: object
}

function useForceUpdate(){
  const [value, setValue] = useState(0); // integer state
  return () => setValue(value => value + 1); // update the state to force render
}

const MFCashFlow: FC<CashFlowProps> = (props) => {
  const [start_month, setStartMonth] = useState<string>(underwritingService.getReportBeginDefault(true, null, true))
  const [term, setTerm] = useState<number>(propertyReportService.getReportTermDefault())
  const [line_item_display, setLineItemDetail] = useState<string>(props.property['assetid'] ? 'Detail Account #' : 'As Entered')
  const [periodicity, setPeriodicity] = useState<number>(12)
  const [account_code, setAccountCode] = useState<boolean>(false)
  const [totals, setTotals] = useState<boolean>(false);
  const [total_metrics, setTotalMetrics] = useState<boolean>(false);
  const forceUpdate = useForceUpdate();

  const periodicityOptions = [
    { label: 'Annual', value: 12 },
    { label: 'Monthly', value: 1 },
    { label: 'Quarterly', value: 3 }
  ]

  useEffect(() => {
    if (periodicity === 12 && totals === true){
      setTotals(false);
    }
    if (periodicity !== 12 && totals === false){
      setTotalMetrics(false)
    }
  }, [periodicity, totals])

  useEffect(() => {
    setStartMonth(underwritingService.getReportBeginDefault(true, null, true));
    setTerm(propertyReportService.getReportTermDefault());
  }, [props['property']])

  function submit():void {
    propertyReportService.propertyReport({
      report: 'multifamily_cash_flow',
      params: {
        asset_type: 'multifamily',
        folder: false,
        detail: false,
        period: periodicity,
        line_item_display,
        start_month: underwritingService.toISOdate(start_month),
        term, account_code, totals, total_metrics
      }
    });
  }

  function updateDate(date:string):void {
    const { adjustedDate, validatedTerm } = propertyReportService.updateDate(date, term);
    setStartMonth(adjustedDate);
    if (validatedTerm) setTerm(validatedTerm);
    forceUpdate();
  }

  function updateTerm(term:number):void {
    const adjustedTerm:number|boolean = propertyReportService.validateTerm(term, start_month);
    if (typeof adjustedTerm === 'number'){
      setTerm(adjustedTerm)
    } else {
      setTerm(term);
    }
    forceUpdate();
  }

  const disabled = !calcService.calcCheck(props.property)
  const buttonText = disabled ? 'Calculation Required' : 'Run Report'
  const lineItemDetailOptions = props.property['assetid'] ? [{label: 'As Entered', value: 'As Entered'},
                                                            {label: 'Detail Account #', value: 'Detail Account #'},
                                                            {label: 'Summary Account #', value: 'Summary Account #'},
                                                            {label: 'Cash Flow Grouping', value: 'Cash Flow Grouping'}]
                                                          : [{label: 'As Entered', value: 'As Entered'},
                                                            {label: 'Cash Flow Grouping', value: 'Cash Flow Grouping'}]

  return (
    <CashFlowReport_s>
      <ButtonWithPress_s primary onClick={submit} style={{marginBottom: 10}} disabled={disabled}>{buttonText}</ButtonWithPress_s>
      <ReportForm_s>
      <div>
          <Label_s>Start Date</Label_s>
          <DateText_s withoutDays value={parseDate(start_month).format('MM/DD/YYYY')} onChange={updateDate} onBlur={updateDate} persist={updateDate} />
        </div>
        <div>
          <Label_s>Term</Label_s>
          <InputNumber_s value={term} onChange={(e) => updateTerm(e.value)} maxFractionDigits={0} />
        </div>
        <div>
          <Label_s>Periodicity</Label_s>
          <DropdownInput
            persist={(field:string, val:number)=>setPeriodicity(val)}
            field="periodicity"
            options={periodicityOptions}
            value={periodicity}
          />
        </div>
        <div>
          <Label_s>Line Item Display</Label_s>
          <DropdownInput
            className="line-item-detail-input"
            persist={(field:string, val:string)=>setLineItemDetail(val)}
            field="line_item_display"
            options={lineItemDetailOptions}
            value={line_item_display}
          />
        </div>
        <div>
          <Label_s>Display Account?</Label_s>
          <CheckboxInput
            field="account_code"
            value={account_code}
            persist={(field:string, val:boolean) => setAccountCode(val)}
          />
        </div>
        <div>
          <Label_s>Display Annual Totals</Label_s>
          <CheckboxInput
            disabled={periodicity === 12}
            field="totals"
            value={totals}
            persist={(field:string, val:boolean) => setTotals(val)}
          />
        </div>
        <div>
          <Label_s>Include Annual Metrics</Label_s>
          <CheckboxInput
            disabled={totals === false && periodicity !== 12}
            field="total_metrics"
            value={total_metrics}
            persist={(field:string, val:boolean) => setTotalMetrics(val)}
          />
        </div>
      </ReportForm_s>
    </CashFlowReport_s>
  )
}

export default MFCashFlow
